// Player identity bits shared with the iOS app: flag emoji and avatar image.
//
// Both fields are OPTIONAL on the wire. built_value omits nulls, so matches
// created before these features existed have no `flagEmoji` / `avatar` key at
// all — every helper here has to tolerate that rather than assume it.

const STORAGE_BUCKET = "wid-tennis.appspot.com";

// Sentinel uuids from the app (lib/constants.dart). A guest is the stand-in
// "Opponent" a match can be created against.
const GUEST_UUID = "42000420-9010-9010-9010-420000000420";
const PERSONA_UUID = "42000420-8010-8010-9010-420000000420";

// The app's fallbacks when a player never picked a country: a black waving
// flag for a real player, a white one for the guest (ExtPlayer.getEmoji).
// Mirrored here so a match looks the same on the web as in the app — most
// players have no flag, so this is the common case, not the edge case.
const FLAG_BLACK = "\u{1F3F4}";
const FLAG_WHITE = "\u{1F3F3}";

export function isGuest(player) {
  return !!player && player.uuid === GUEST_UUID;
}

export function isPersona(player) {
  return !!player && player.uuid === PERSONA_UUID;
}

// `flagEmoji` is stored as comma-separated HEX code points, e.g.
// "1F1FA,1F1F8" (🇺🇸). Sequences can include a variation selector and a ZWJ
// — "1F3F3,FE0F,200D,1F308" is 🏳️‍🌈 — so all points must be applied
// together, not rendered one by one.
export function flagEmoji(player) {
  const raw = player && player.flagEmoji;
  // No country set — fall back exactly as the app does, rather than leaving
  // the avatar bare.
  if (!raw) return isGuest(player) ? FLAG_WHITE : FLAG_BLACK;

  try {
    const points = String(raw)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((hex) => parseInt(hex, 16));

    if (!points.length || points.some((n) => !Number.isFinite(n))) {
      return isGuest(player) ? FLAG_WHITE : FLAG_BLACK;
    }
    return String.fromCodePoint(...points);
  } catch (e) {
    return isGuest(player) ? FLAG_WHITE : FLAG_BLACK;
  }
}

export function playerInitials(player) {
  if (!player) return "";
  const first = (player.firstName || "").trim();
  const last = (player.lastName || "").trim();
  if (!first && !last) return "";
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

// Avatars live in Firebase Storage at avatars/{playerUUID}_{version}, where
// the version is the player's `avatar` field — so the URL changes whenever the
// photo does and no cache busting is needed.
//
// Returns null when the player has no avatar, which the UI renders as an
// initials circle (same fallback as the app).
export function avatarUrl(player) {
  if (!player || !player.uuid || !player.avatar) return null;

  const path = encodeURIComponent(`avatars/${player.uuid}_${player.avatar}`);
  return `https://firebasestorage.googleapis.com/v0/b/${STORAGE_BUCKET}/o/${path}?alt=media`;
}
